import { useEffect, useRef, useState } from 'react';
import {
  Autocomplete, Badge, Box, Button, Chip, CircularProgress, Collapse, IconButton,
  Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import UndoIcon from '@mui/icons-material/Undo';
import HistoryIcon from '@mui/icons-material/History';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ACCENT, linkifyText } from './offerteUi';
import { buildGrippOfferUrl } from '../../utils/grippDeepLink';
import { OfferteInkijk, TekstMetOffertes } from './OfferteInkijk';

/**
 * De assistent als balk onderin het offerte-document (sticky). De chat is niet
 * weg — de thread klapt open boven de balk (💬) en de laatste assistent-reactie
 * is altijd als preview zichtbaar, zodat je een vraag van de assistent nooit
 * mist zonder dat het gesprek permanent 50% van het scherm inneemt.
 *
 * Sticky-voorwaarde: deze component moet een DIRECTE child van het
 * document-Paper zijn (geen overflow:hidden ertussen); de echte
 * scroll-container is <Box component="main"> in AppShell.
 */
// Eén chat-bericht; lange GEBRUIKERS-prompts (geplakte mails/notulen) starten
// ingeklapt (6 regels) met een "toon alles"-knop. Antwoorden van de assistent
// blijven volledig zichtbaar — die wil je juist lezen (feedback 20 aug).
function Bericht({ m, onOpenOfferte }) {
  const [open, setOpen] = useState(false);
  const inhoud = m.content || '';
  // "Lang" op tekens ÉN regels: tien korte regels nemen net zoveel ruimte als
  // één lap tekst.
  const lang = m.role === 'user' && (inhoud.length > 450 || inhoud.split('\n').length > 8);
  return (
    <Box sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', mb: 1 }}>
      <Paper elevation={0} sx={{
        px: 1.5, py: 1, maxWidth: '85%', borderRadius: 2,
        bgcolor: m.role === 'user' ? '#6366f1' : '#f3f4f6',
        color: m.role === 'user' ? '#fff' : '#1f2937',
      }}>
        <Typography variant="body2" sx={{
          whiteSpace: 'pre-wrap',
          ...(lang && !open ? { display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}),
        }}>
          {m.role === 'assistant'
            ? <TekstMetOffertes tekst={m.content} onOpen={onOpenOfferte} render={linkifyText} />
            : linkifyText(m.content)}
        </Typography>
        {lang && (
          <Typography variant="caption" onClick={() => setOpen((v) => !v)}
            sx={{ color: '#e0e7ff', cursor: 'pointer', fontWeight: 700, display: 'block', mt: 0.25 }}>
            {open ? 'inklappen' : 'toon alles'}
          </Typography>
        )}
      </Paper>
    </Box>
  );
}

function AssistentBalk({
  messages, prefill, invoerRef, onSend, chatLoading,
  vragen = [], notes = '',
  references = [], onRemoveReference, refOpts, refLoading, onSearchOffers, onAddReference,
  showForceDraft = false, onForceDraft,
  threadOpen, onThreadToggle,
  canUndo = false, onUndo,
  scope = 'all', onScopeChange, versieCount = 0, onVersies,
}) {
  const endRef = useRef(null);
  // Invoer leeft lokaal: typen rendert alleen deze balk, niet de hele generator.
  // invoerRef (optioneel) is de spiegel in de ouder: daaruit starten we bij een
  // remount en daarin schrijven we elke wijziging, zonder dat de ouder rendert.
  const [chatInput, setChatInputState] = useState(() => invoerRef?.current ?? prefill?.tekst ?? '');
  const setChatInput = (tekst) => {
    setChatInputState(tekst);
    if (invoerRef) invoerRef.current = tekst;
  };
  const prefillN = useRef(prefill?.n);
  useEffect(() => {
    if (!prefill || prefill.n === prefillN.current) return;
    prefillN.current = prefill.n;
    setChatInputState(prefill.tekst || '');
  }, [prefill]);
  const verstuur = () => { if (onSend(chatInput)) setChatInput(''); };
  const [showRefPicker, setShowRefPicker] = useState(false);
  const [vragenOpen, setVragenOpen] = useState(true);
  const [inkijkNummer, setInkijkNummer] = useState(null); // eerdere offerte bekijken
  // Nieuwe openstaande vragen mogen niet verstopt blijven achter een eerder
  // dichtgeklapt blok.
  const vragenKey = JSON.stringify([vragen, notes]);
  useEffect(() => { if (vragen.length || notes) setVragenOpen(true); }, [vragenKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (threadOpen) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading, threadOpen]);

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');

  return (
    <Box sx={{
      position: { xs: 'static', md: 'sticky' }, bottom: 0, zIndex: 2,
      bgcolor: '#fff', borderTop: '1px solid #e5e7eb',
      borderBottomLeftRadius: 8, borderBottomRightRadius: 8,
      px: { xs: 2, md: 3 }, py: 1.5,
    }}>
      {/* Openstaande punten van de assistent — dit verving de gele Alert-stapel. */}
      {(vragen.length > 0 || notes) && (
        <Box sx={{ mb: 1, p: 1, px: 1.25, borderRadius: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
          <Stack direction="row" alignItems="center" spacing={0.5} onClick={() => setVragenOpen((v) => !v)}
            sx={{ cursor: 'pointer', userSelect: 'none' }}>
            <Typography variant="caption" fontWeight={800} sx={{ color: '#92400e' }}>
              Nog navragen / controleren{vragen.length ? ` (${vragen.length})` : ''}
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: 15, color: '#b45309', transform: vragenOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
          </Stack>
          <Collapse in={vragenOpen}>
            {vragen.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {vragen.map((v, i) => <li key={i}><Typography variant="caption" sx={{ color: '#78350f' }}><TekstMetOffertes tekst={v} onOpen={setInkijkNummer} /></Typography></li>)}
              </Box>
            )}
            {notes && <Typography variant="caption" sx={{ color: '#78350f', display: 'block' }}><TekstMetOffertes tekst={notes} onOpen={setInkijkNummer} /></Typography>}
          </Collapse>
        </Box>
      )}

      {/* De thread, opengeklapt boven de balk */}
      <Collapse in={threadOpen} unmountOnExit>
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75} sx={{ mb: 1 }}>
            <Button size="small" variant="text" onClick={() => setShowRefPicker((v) => !v)}
              sx={{ textTransform: 'none', fontWeight: 700, color: '#4338ca' }}>
              📎 {showRefPicker ? 'Verberg referentie-zoeker' : 'Oude offerte erbij pakken (referentie)'}
            </Button>
            {references.map((r) => (
              <Chip key={r.number} size="small"
                label={r.id ? (
                  <a href={buildGrippOfferUrl(r.id)} target="_blank" rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                    title="Open deze offerte in Gripp om te vergelijken">
                    {r.number} — {r.subject} ↗
                  </a>
                ) : `${r.number} — ${r.subject}`}
                onDelete={() => onRemoveReference(r.number)} sx={{ bgcolor: '#eef2ff', color: '#4338ca', maxWidth: 340 }} />
            ))}
          </Stack>
          {showRefPicker && (
            <Autocomplete
              size="small" options={refOpts} value={null} blurOnSelect clearOnBlur
              loading={refLoading} filterOptions={(x) => x}
              getOptionLabel={(o) => `${o.number} — ${o.subject}`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onInputChange={(_e, val, reason) => { if (reason === 'input') onSearchOffers(val); }}
              onChange={(_e, val) => { if (val) { onAddReference(val); setShowRefPicker(false); } }}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{o.number} — {o.subject}</Typography>
                    <Typography variant="caption" color="text.secondary">{[o.company, o.contact, o.date].filter(Boolean).join(' · ')}</Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => <TextField {...params} autoFocus label="Oude offertes zoeken" placeholder="Zoek op klant, contactpersoon of onderwerp…" />}
              noOptionsText="Typ om te zoeken"
              sx={{ mb: 1.5 }}
            />
          )}
          <Box sx={{ maxHeight: '42vh', overflowY: 'auto', pr: 0.5 }}>
            {messages.map((m, i) => <Bericht key={`${i}-${m.role}-${(m.content || '').slice(0, 24)}`} m={m} onOpenOfferte={setInkijkNummer} />)}
            {chatLoading && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}><CircularProgress size={14} /><Typography variant="caption">Assistent denkt na…</Typography></Box>}
            <div ref={endRef} />
          </Box>
        </Box>
      </Collapse>

      {/* Preview van de laatste assistent-reactie als de thread dicht is */}
      {!threadOpen && lastAssistant && (
        <Box onClick={onThreadToggle} sx={{
          mb: 1, p: 1, px: 1.25, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e5e7eb',
          cursor: 'pointer', '&:hover': { bgcolor: '#eef2ff' },
        }}>
          {/* Volledig antwoord, niet afgekapt — een vraag van de assistent moet je
              helemaal kunnen lezen (feedback 24 aug). Scrollt binnen de balk. */}
          <Typography variant="caption" sx={{
            color: '#475569', whiteSpace: 'pre-wrap', display: 'block',
            maxHeight: '38vh', overflowY: 'auto',
          }}>
            {lastAssistant.content}
          </Typography>
          <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 700 }}>
            volledig gesprek tonen ↑
          </Typography>
        </Box>
      )}
      {!threadOpen && chatLoading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, color: 'text.secondary' }}>
          <CircularProgress size={14} /><Typography variant="caption">Assistent denkt na…</Typography>
        </Stack>
      )}

      {/* De balk zelf */}
      <Stack direction="row" spacing={1} alignItems="flex-end">
        <Tooltip title={threadOpen ? 'Gesprek inklappen' : 'Volledig gesprek tonen'}>
          <IconButton size="small" onClick={onThreadToggle} sx={{ mb: 0.25, color: threadOpen ? ACCENT.main : '#64748b' }}>
            <Badge badgeContent={threadOpen ? 0 : messages.length} max={99}
              sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14, bgcolor: ACCENT.soft, color: ACCENT.dark } }}>
              {threadOpen ? <ExpandMoreIcon /> : <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />}
            </Badge>
          </IconButton>
        </Tooltip>
        <TextField fullWidth multiline maxRows={4} size="small"
          placeholder="Vraag een wijziging of beantwoord de vraag van de assistent…"
          value={chatInput} onChange={(e) => setChatInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); verstuur(); } }} />
        {canUndo && (
          <Tooltip title="Laatste assistent-wijziging ongedaan maken (vorige versie van het concept terug)">
            <IconButton size="small" onClick={onUndo} sx={{ mb: 0.25, color: '#64748b' }}>
              <UndoIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        <Button variant="contained" onClick={verstuur} disabled={chatLoading || !chatInput.trim()}
          sx={{ minWidth: 0, px: 1.5, bgcolor: ACCENT.main, '&:hover': { bgcolor: ACCENT.dark } }}>
          <SendIcon fontSize="small" />
        </Button>
      </Stack>
      {/* Scope-slot + versies (4 sep): wat de assistent mág aanpassen staat zichtbaar en
          gaat met élk bericht mee — een "alleen omschrijving" uit een eerder bericht
          verdween anders uit het contextvenster. */}
      {onScopeChange && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.75 }}>
          <Tooltip title="Wat mag de assistent aanpassen? Wordt automatisch gezet als je 'alleen de omschrijving' of 'alleen de regels' typt, en blijft staan tot je het wijzigt.">
            <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_e, v) => v && onScopeChange(v)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 11.5, py: 0.25, px: 1, fontWeight: 700 },
                '& .Mui-selected': { bgcolor: scope === 'all' ? undefined : '#fef3c7 !important', color: scope === 'all' ? undefined : '#92400e !important' } }}>
              <ToggleButton value="all">Alles mag</ToggleButton>
              <ToggleButton value="description">🔒 Alleen omschrijving</ToggleButton>
              <ToggleButton value="lines">🔒 Alleen regels</ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          {onVersies && (
            <Button size="small" startIcon={<HistoryIcon sx={{ fontSize: 16 }} />} onClick={onVersies}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, color: '#475569' }}>
              Versies{versieCount ? ` (${versieCount})` : ''}
            </Button>
          )}
        </Stack>
      )}
      {showForceDraft && (
        <Button size="small" sx={{ mt: 0.5, textTransform: 'none' }} onClick={onForceDraft} disabled={chatLoading}>
          Maak nu alvast een concept (met wat bekend is) →
        </Button>
      )}
      <OfferteInkijk nummer={inkijkNummer} onClose={() => setInkijkNummer(null)} />
    </Box>
  );
}

export default AssistentBalk;
